import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    CircularProgress,
    Button,
    Divider,
    LinearProgress,
    Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import EditNoteIcon from '@mui/icons-material/EditNote';
import RecommendIcon from '@mui/icons-material/Recommend';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
    getEditorialIntelligenceById,
    getRelatedEditorialIntelligences,
} from '../../../utils/api';
import { createNotification } from '../../../utils/alerts';
import AdminDataTable from '../../../components/admin/AdminDataTable';
import AdminPagination from '../../../components/admin/AdminPagination';
import ResponsiveAdminPage from '../../../components/admin/ResponsiveAdminPage';
import './editorial-intelligence.scss';

/* ─── Helpers ─────────────────────────────────────────────────── */
const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const pct = (val) => (val == null ? null : `${(val * 100).toFixed(0)}%`);
const boolLabel = (val) => (val == null ? null : val ? 'Sim' : 'Não');
const PRIMARY = '#f25d27';

const formatTransactionValue = (tv) => {
    if (!tv) return null;
    const formatted = Number(tv.amount).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return `${formatted} ${tv.currency}${tv.mention_intent ? ` (${tv.mention_intent})` : ''}`;
};

const formatCompanyName = (company) => (
    company.ticker ? `${company.name} (${company.ticker})` : company.name
);

const formatRecommendation = (value) => {
    if (!value) return '—';
    if (typeof value === 'string') return value;
    return value.primary_area || '—';
};

/* ─── Sub-components ──────────────────────────────────────────── */
const Field = ({ label, value }) => {
    if (value == null || value === '') return null;
    return (
        <Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, mb: 0.3 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.5, wordBreak: 'break-word' }}>
                {value}
            </Typography>
        </Box>
    );
};

const ListField = ({ label, values }) => {
    if (!values?.length) return null;
    return (
        <Box>
            <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, mb: 0.3 }}>
                {label}
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                {values.map((v, i) => (
                    <Typography key={i} component="li" sx={{ fontSize: '0.9rem', color: '#333', lineHeight: 1.6 }}>
                        {v}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};

const ChipField = ({ label, value, chipSx }) => {
    if (value == null || value === '') return null;
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 1.2,
                minHeight: 28,
            }}
        >
            <Typography sx={{ fontSize: '0.82rem', color: '#666', minWidth: { sm: 140 }, flexShrink: 0 }}>
                {label}
            </Typography>
            <Chip label={value} size="small" sx={{ fontSize: '0.78rem', fontWeight: 500, ...chipSx }} />
        </Box>
    );
};

const CompactMetaField = ({ label, value }) => (
    <Typography sx={{ fontSize: '0.74rem', color: '#555', lineHeight: 1.3 }}>
        <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Box>{' '}
        {value || '—'}
    </Typography>
);

// ── Tags de classificação (Setor + Recomendação + Materialidade) ─────────────
// Usadas tanto na coluna "sem label" da tabela de análises relacionadas quanto
// no card mobile, para que ambas as visualizações fiquem consistentes.
const relatedClassificationBadgeSx = {
    height: 22,
    fontSize: '0.72rem',
    '& .MuiChip-label': { px: 1 },
};

const ClassificationField = ({ label, value, sx }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
        <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#8a8a8a' }}>
            {label}:
        </Typography>
        <Chip label={value || '—'} size="small" variant="outlined" sx={{ ...relatedClassificationBadgeSx, ...sx }} />
    </Box>
);

// `stacked` força Setor/Recomendação/Materialidade uma abaixo da outra,
// independentemente da largura disponível — usado apenas na tabela. No card
// mobile o padrão (lado a lado, com quebra) é mantido.
const ClassificationTags = ({ row, stacked = false }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: stacked ? 'column' : 'row',
            flexWrap: stacked ? 'nowrap' : 'wrap',
            alignItems: stacked ? 'flex-start' : 'center',
            gap: stacked ? 0.5 : 0.8,
        }}
    >
        <ClassificationField label="Setor" value={row.sector} />
        <ClassificationField
            label="Recomendação"
            value={formatRecommendation(row.publication_recommendation)}
            sx={{ color: PRIMARY, borderColor: PRIMARY }}
        />
        <ClassificationField
            label="Materialidade"
            value={row.materiality_level}
            sx={{ backgroundColor: '#eee' }}
        />
    </Box>
);

const RelatedEditorialCompactCard = ({ row, onClick }) => {
    return (
        <Paper
            elevation={0}
            onClick={onClick}
            sx={{
                p: 1.2,
                borderRadius: '8px',
                border: '1px solid #ececec',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { backgroundColor: `${PRIMARY}08` } : undefined,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#8a8a8a' }}>
                        Fonte:
                    </Typography>
                    <Chip
                        label={row.source || '—'}
                        size="small"
                        sx={{
                            backgroundColor: PRIMARY,
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.7rem',
                            height: 22,
                            maxWidth: 'calc(100% - 56px)',
                            '& .MuiChip-label': { px: 1.1, overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                    />
                </Box>

                <Typography sx={{ fontSize: '0.78rem', color: '#222', lineHeight: 1.35 }}>
                    <Box component="span" sx={{ fontWeight: 700, color: '#8a8a8a' }}>
                        Titulo:
                    </Box>{' '}
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 600,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            verticalAlign: 'top',
                        }}
                    >
                        {row.title || '—'}
                    </Box>
                </Typography>

                <CompactMetaField label="Tema" value={row.theme || '—'} />

                <CompactMetaField label="Radar" value={row.radar_keyword || '—'} />

                <ClassificationTags row={row} />

                <CompactMetaField label="Data de publicação" value={formatDate(row.published_at)} />
            </Box>
        </Paper>
    );
};

const Section = ({ icon, title, children }) => (
    <Paper sx={{ p: '20px 24px', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            {icon && <Box sx={{ color: '#f25d27', display: 'flex' }}>{icon}</Box>}
            <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                {title}
            </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        {children}
    </Paper>
);

/* ─── Page ────────────────────────────────────────────────────── */
export default function AdminEditorialIntelligenceDetailPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { editorial_intelligence_id } = useParams();
    const isMobile = useMediaQuery('(max-width:600px)');
    const isRelatedCards = useMediaQuery('(max-width:1360px)');

    // Estado de navegação recebido ao navegar para cá — repassado ao voltar
    const navigationState = location.state || null;
    const shouldReturnToDashboard = Boolean(
        navigationState?.fromDashboard && navigationState?.dashboardState
    );

    const navigateBackToListing = () => {
        if (shouldReturnToDashboard) {
            navigate('/admin/dashboards', {
                state: { dashboardState: navigationState.dashboardState },
            });
            return;
        }

        if (navigationState) {
            navigate('/admin/editorial-intelligence', { state: navigationState });
            return;
        }

        navigate('/admin/editorial-intelligence');
    };

    const [item, setItem] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedItems, setRelatedItems] = useState([]);
    const [relatedTotal, setRelatedTotal] = useState(0);
    const [relatedLoading, setRelatedLoading] = useState(false);
    const [relatedPage, setRelatedPage] = useState(0);
    const [relatedPageSize, setRelatedPageSize] = useState(10);

    useEffect(() => {
        fetchItem();
        setRelatedPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorial_intelligence_id]);

    useEffect(() => {
        fetchRelatedItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editorial_intelligence_id, relatedPage, relatedPageSize]);

    const fetchItem = async () => {
        setLoading(true);
        try {
            const data = await getEditorialIntelligenceById(editorial_intelligence_id);
            setItem(data);
        } catch (err) {
            if (err.response?.status === 404) {
                createNotification('error', 'Artigo não encontrado.', isMobile);
                navigateBackToListing();
            } else if (err.response?.status !== 401) {
                createNotification('error', 'Erro ao carregar artigo.', isMobile);
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchRelatedItems = async () => {
        setRelatedLoading(true);
        try {
            const data = await getRelatedEditorialIntelligences(editorial_intelligence_id, {
                page: relatedPage,
                page_size: relatedPageSize,
            });
            setRelatedItems(data.editorial_intelligences ?? []);
            setRelatedTotal(data.total ?? 0);
        } catch (err) {
            if (err.response?.status !== 401) {
                createNotification('error', 'Erro ao carregar análises relacionadas.', isMobile);
            }
        } finally {
            setRelatedLoading(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress sx={{ color: '#f25d27' }} />
            </Box>
        );
    }

    if (!item) return null;

    const transactionValues = Array.isArray(item.transaction_values) ? item.transaction_values : [];
    const hasCompanies = item.companies_mentioned?.length > 0;
    const hasTransaction = transactionValues.length > 0;
    const hasRelevantFacts = item.relevant_facts?.length > 0;
    const hasRegulatoryMentions = item.regulatory_mentions?.length > 0;
    const hasRisksOrConditions = item.risks_or_conditions?.length > 0;
    const hasMentionedAdvisors = item.mentioned_advisors?.length > 0;
    const hasPubRec = Boolean(item.publication_recommendation);
    const rec = item.publication_recommendation;
    const confidence = item.confidence != null ? Math.round(item.confidence * 100) : null;
    const materialityReason = item.materiality_level_reason ?? item.meteriality_level_reason;
    const hasRelevantDetails = hasRelevantFacts || hasCompanies || hasTransaction || hasRegulatoryMentions || hasRisksOrConditions || hasMentionedAdvisors;
    const relatedColumns = [
        {
            id: 'source',
            field: 'source',
            label: 'Fonte',
            minWidth: 120,
            sortable: false,
            render: (row) => (
                <Chip
                    label={row.source || '—'}
                    size="small"
                    sx={{ backgroundColor: PRIMARY, color: '#fff', fontWeight: 600, fontSize: '0.75rem' }}
                />
            ),
        },
        { id: 'title', field: 'title', label: 'Título', minWidth: 220, sortable: false, render: (row) => row.title || '—' },
        { id: 'theme', field: 'theme', label: 'Tema', minWidth: 140, sortable: false, render: (row) => row.theme || '—' },
        {
            id: 'published_at',
            field: 'published_at',
            label: 'Data de publicação',
            minWidth: 155,
            sortable: false,
            render: (row) => formatDate(row.published_at),
        },
        { id: 'radar_keyword', field: 'radar_keyword', label: 'Radar', minWidth: 120, sortable: false, render: (row) => row.radar_keyword || '—' },
        {
            id: 'classification',
            field: null,
            label: 'Classificações',
            minWidth: 220,
            sortable: false,
            render: (row) => <ClassificationTags row={row} stacked />,
        },
    ];

    return (
        <ResponsiveAdminPage>
            {/* Voltar */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={navigateBackToListing}
                sx={{ color: '#434851', textTransform: 'none', mb: 2, pl: 0 }}
            >
                Voltar para listagem
            </Button>

            {/* ── HEADER ─────────────────────────────────────────────── */}
            <Paper sx={{ p: '24px 28px', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', mb: 2, borderLeft: '4px solid #f25d27' }}>

                {/* Linha 1 — chips + data */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                    <Tooltip title="Fonte" arrow>
                        <Chip
                            label={item.source}
                            size="small"
                            sx={{ backgroundColor: '#f25d27', color: '#fff', fontWeight: 600, cursor: 'default' }}
                        />
                    </Tooltip>
                    {item.sector && (
                        <Tooltip title="Setor" arrow>
                            <Chip
                                label={item.sector}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: '#ddd', color: '#555', cursor: 'default' }}
                            />
                        </Tooltip>
                    )}
                    {item.publication_recommendation?.primary_area && (
                        <Tooltip title="Recomendação" arrow>
                            <Chip
                                label={item.publication_recommendation.primary_area}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: '#f25d27', color: '#f25d27', fontWeight: 600, cursor: 'default' }}
                            />
                        </Tooltip>
                    )}
                    {item.radar_keyword && (
                        <Tooltip title="Radar" arrow>
                            <Chip
                                label={item.radar_keyword}
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: '#f25d27', color: '#f25d27', cursor: 'default' }}
                            />
                        </Tooltip>
                    )}
                    <Typography variant="caption" sx={{ color: '#999', ml: 'auto' }}>
                        Publicado em {formatDate(item.published_at)}
                    </Typography>
                </Box>

                {/* Linha 2 — Título */}
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.4, mb: item.theme ? 0.75 : (item.url ? 1.5 : 0) }}>
                    {item.title}
                </Typography>

                {/* Linha 3 — Tema */}
                {item.theme && (
                    <Typography sx={{ color: '#666', fontSize: '0.92rem', mb: item.url ? 1.5 : 0 }}>
                        {item.theme}
                    </Typography>
                )}

                {/* Linha 4 — Botões de ação */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
                    <Button
                        variant="contained"
                        startIcon={<EditNoteIcon fontSize="small" />}
                        onClick={() =>
                            navigate(
                                `/admin/editorial-intelligence/${editorial_intelligence_id}/write-article`,
                                { state: navigationState }
                            )
                        }
                        sx={{
                            backgroundColor: '#f25d27',
                            '&:hover': { backgroundColor: '#d44e1f' },
                            textTransform: 'none',
                            boxShadow: 'none',
                            fontWeight: 600,
                        }}
                    >
                        Escrever matéria
                    </Button>
                    {item.url && (
                        <Button
                            variant="text"
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            sx={{ color: '#f25d27', textTransform: 'none', px: 0, fontWeight: 500 }}
                        >
                            Acessar conteúdo
                        </Button>
                    )}

                </Box>
            </Paper>

            <Grid container spacing={2}>

                {/* ── LINHA 2: RESUMO ─────────────────────────────────── */}
                {item.summary && (
                    <Grid item xs={12}>
                        <Section icon={null} title="Resumo">
                            <Typography sx={{ color: '#444', fontSize: '0.95rem', lineHeight: 1.8 }}>
                                {item.summary}
                            </Typography>
                        </Section>
                    </Grid>
                )}

                {/* ── LINHA 3: RECOMENDAÇÃO + ANÁLISE ─────────────────── */}

                {/* Recomendação de Publicação */}
                {hasPubRec && (
                    <Grid item xs={12} md={6}>
                        <Section icon={<RecommendIcon />} title="Recomendação de Publicação">
                            <Box sx={{ mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
                                    <Field label="Área Principal" value={rec.primary_area} />
                                    <Typography sx={{ fontSize: '0.8rem', color: '#666', ml: 1, whiteSpace: 'nowrap' }}>
                                        {pct(rec.confidence)} de confiança
                                    </Typography>
                                </Box>
                                <LinearProgress
                                    variant="determinate"
                                    value={Math.round((rec.confidence ?? 0) * 100)}
                                    sx={{
                                        height: 6, borderRadius: 3,
                                        backgroundColor: '#eee',
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: rec.confidence >= 0.7 ? '#2e7d32' : rec.confidence >= 0.4 ? '#ed6c02' : '#d32f2f',
                                            borderRadius: 3,
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Field label="Justificativa" value={rec.rationale} />
                                <ListField label="Sinais Editoriais" values={rec.matched_editorial_signals} />

                                {rec.alternative_areas?.length > 0 && (
                                    <Box>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500, mb: 0.5 }}>
                                            Áreas Alternativas
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {rec.alternative_areas.map((alt, i) => (
                                                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                    <Typography sx={{ fontSize: '0.85rem', color: '#333', pt: '2px' }}>
                                                        {alt.area}
                                                    </Typography>
                                                    {alt.rationale && (
                                                        <Typography sx={{ fontSize: '0.85rem', color: '#666', pt: '2px' }}>
                                                            {alt.rationale}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Section>
                    </Grid>
                )}

                {/* Análise */}
                <Grid item xs={12} md={hasPubRec ? 6 : 12}>
                    <Section icon={<AnalyticsIcon />} title="Análise">
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <ChipField
                                label="Sentimento"
                                value={item.document_sentiment}
                                chipSx={{ backgroundColor: '#e8f0fe', color: '#1a56db' }}
                            />
                            <ChipField
                                label="Materialidade"
                                value={item.materiality_level}
                                chipSx={{ backgroundColor: 'rgba(0,0,0,0.06)', color: '#444' }}
                            />
                            <Field label="Justificativa" value={materialityReason} />
                            <ChipField
                                label="Revisão Humana"
                                value={boolLabel(item.requires_human_review)}
                                chipSx={{
                                    backgroundColor: item.requires_human_review ? 'rgba(211,47,47,0.1)' : 'rgba(46,125,50,0.1)',
                                    color: item.requires_human_review ? '#c62828' : '#2e7d32',
                                }}
                            />
                            <ChipField
                                label="Radar"
                                value={boolLabel(item.radar)}
                                chipSx={{
                                    backgroundColor: item.radar ? 'rgba(242,93,39,0.12)' : 'rgba(0,0,0,0.06)',
                                    color: item.radar ? '#f25d27' : '#444',
                                }}
                            />
                            {item.human_review_reason && (
                                <Field label="Motivo da Revisão Humana" value={item.human_review_reason} />
                            )}

                            {confidence != null && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#666' }}>
                                            Confiança
                                        </Typography>
                                        <Typography sx={{
                                            fontSize: '0.82rem', fontWeight: 700,
                                            color: confidence >= 70 ? '#2e7d32' : confidence >= 40 ? '#e65100' : '#c62828',
                                        }}>
                                            {confidence}%
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={confidence}
                                        sx={{
                                            height: 6, borderRadius: 3,
                                            backgroundColor: '#eee',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: confidence >= 70 ? '#2e7d32' : confidence >= 40 ? '#ed6c02' : '#d32f2f',
                                                borderRadius: 3,
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                            <Field label="Justificativa" value={item.confidence_reason} />

                            {item.evidences?.length > 0 && (
                                <>
                                    <Divider sx={{ my: 0.5 }} />
                                    <ListField label="Evidências" values={item.evidences} />
                                </>
                            )}
                        </Box>
                    </Section>
                </Grid>

                {/* ── LINHA 4: FATOS RELEVANTES ───────────────────────── */}
                {hasRelevantDetails && (
                    <Grid item xs={12}>
                        <Section icon={<DescriptionOutlinedIcon />} title="Fatos Relevantes">
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.2 }}>
                                {hasRelevantFacts && (
                                    <ListField label="Fatos Relevantes" values={item.relevant_facts} />
                                )}

                                {hasCompanies && (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Typography sx={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 500 }}>
                                            Empresas Mencionadas
                                        </Typography>
                                        {item.companies_mentioned.map((company, i) => (
                                            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: '0.85rem', color: '#333', pt: '2px' }}>
                                                    {formatCompanyName(company)}
                                                </Typography>
                                                {company.mention_intent && (
                                                    <Typography sx={{ fontSize: '0.85rem', color: '#666', pt: '2px' }}>
                                                        {company.mention_intent}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {hasTransaction && (
                                    <ListField
                                        label="Valor Mencionado"
                                        values={transactionValues.map(formatTransactionValue).filter(Boolean)}
                                    />
                                )}

                                {hasRisksOrConditions && (
                                    <ListField label="Riscos ou Condições" values={item.risks_or_conditions} />
                                )}

                                {hasRegulatoryMentions && (
                                    <ListField label="Menções Regulatórias" values={item.regulatory_mentions} />
                                )}

                                {hasMentionedAdvisors && (
                                    <ListField label="Assessores Mencionados" values={item.mentioned_advisors} />
                                )}
                            </Box>
                        </Section>
                    </Grid>
                )}

            </Grid>

            <Paper sx={{ borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', mt: 2 }}>
                <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                        Análises relacionadas
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999' }}>
                        Outras inteligências editoriais relacionadas a esta análise
                    </Typography>
                </Box>
                {relatedLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: PRIMARY }} />
                    </Box>
                ) : (
                    <Box sx={{ p: isRelatedCards ? 1 : 0 }}>
                        <AdminDataTable
                            columns={relatedColumns}
                            rows={relatedItems}
                            orderBy=""
                            orderDirection="asc"
                            onSort={() => {}}
                            emptyMessage="Nenhuma análise relacionada encontrada."
                            mobileBreakpoint={1360}
                            onRowClick={(row) => row.id && navigate(`/admin/editorial-intelligence/${row.id}`, { state: navigationState })}
                            mobileCardRenderer={(row, index) => (
                                <RelatedEditorialCompactCard
                                    key={row.id ?? index}
                                    row={row}
                                    onClick={row.id ? () => navigate(`/admin/editorial-intelligence/${row.id}`, { state: navigationState }) : undefined}
                                />
                            )}
                        />
                    </Box>
                )}
                <AdminPagination
                    page={relatedPage}
                    pageSize={relatedPageSize}
                    total={relatedTotal}
                    onPageChange={setRelatedPage}
                    onPageSizeChange={(size) => { setRelatedPageSize(size); setRelatedPage(0); }}
                />
            </Paper>

            {/* ── FOOTER ──────────────────────────────────────────────── */}
            <Paper sx={{ p: '12px 20px', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    <Typography variant="caption" sx={{ color: '#aaa' }}>
                        Criado em{' '}
                        <Box component="strong" sx={{ color: '#666' }}>{formatDate(item.created_at)}</Box>
                    </Typography>
                    {item.updated_at && (
                        <Typography variant="caption" sx={{ color: '#aaa' }}>
                            Atualizado em{' '}
                            <Box component="strong" sx={{ color: '#666' }}>{formatDate(item.updated_at)}</Box>
                        </Typography>
                    )}
                </Box>
            </Paper>
        </ResponsiveAdminPage>
    );
}
